import styled from 'styled-components';
import Lightning from '../assets/images/icon-lightning.svg';
import Button from '../components/Button';
import {Form, Field, useFormState} from 'react-final-form';
import * as Validation from '../utils/form-validation';
import {useTranslation} from 'react-i18next';
import i18next from 'i18next';
import {FORM_ERROR} from 'final-form';
import {onboarding} from '../utils/api';

import {
  FormSubtitle,
  FormTitle,
  FormInput,
  FormSelect,
  FormError,
  FormCheckbox,
  FormRadioButton,
  FormInputLabel,
} from './FormElements';

interface OnboardingProps {
  setGameStatus: Function;
  sessionToken: string;
  showAutoFlagReport?: boolean;
  customFields?: [];
}

export default function Onboarding({
  setGameStatus,
  sessionToken,
  showAutoFlagReport,
  customFields,
}: OnboardingProps) {
  const {t} = useTranslation();
  const langUser = i18next.language as LangUserT;

  const handleSubmit = async (formData: any) => {
    try {
      const response = await onboarding(formData, sessionToken);
      console.log(response);
      setGameStatus('playing');
    } catch (error) {
      console.log(error);
      return {[FORM_ERROR]: t('form.generic-error')};
    }
  };

  const TheForm = () => {
    return (
      <>
        <FormTitle>{t('onboarding.form.title')}</FormTitle>
        <FormSubtitle>{t('onboarding.form.subtitle')}</FormSubtitle>
        <Form
          onSubmit={handleSubmit}
          initialValues={{
            automaticReport: false,
          }}
          render={({handleSubmit, submitting, submitError, errors}) => {
            return (
              <form onSubmit={handleSubmit}>
                <FormInner>
                  <Field
                    name="name"
                    label={t('onboarding.form.name')}
                    component={FormInput}
                    placeholder={t('onboarding.form.name')}
                    validate={Validation.required}
                  />
                  <Field
                    name="surname"
                    label={t('onboarding.form.lastname')}
                    component={FormInput}
                    placeholder={t('onboarding.form.lastname')}
                    validate={Validation.required}
                  />
                  <Field
                    name="birth"
                    label={t('onboarding.form.yearofbirth')}
                    component={FormInput}
                    placeholder={t('onboarding.form.yearofbirth')}
                    validate={Validation.requireBirthDate}
                  />
                  <Field
                    name="gender"
                    label={t('onboarding.form.gender')}
                    component={FormSelect}
                    validate={Validation.required}
                    options={[
                      {
                        label: t('onboarding.form.female'),
                        value: 'female',
                      },
                      {
                        label: t('onboarding.form.male'),
                        value: 'male',
                      },
                      {
                        label: t('onboarding.form.other'),
                        value: 'other',
                      },
                      {
                        label: t('onboarding.form.no-binary'),
                        value: 'no-binary',
                      },
                      {
                        label: t('onboarding.form.unknown'),
                        value: 'unknown',
                      },
                    ]}
                  />
                </FormInner>
                <FormInnerCustomsFields>
                  {customFields?.map((field: any, index) => {
                    const labels = {
                      it: field.labelIt,
                      en: field.labelEn,
                      es: field.labelEs,
                      pl: field.labelPl,
                    };

                    if (field.type === 'text') {
                      return (
                        <Field
                          key={index}
                          name={field.name}
                          label={labels[langUser]}
                          placeholder={labels[langUser]}
                          component={FormInput}
                          maxLength="254"
                          validate={
                            field.required ? Validation.required : () => false
                          }
                        />
                      );
                    }

                    if (field.type === 'select') {
                      return (
                        <MultipleChoiceComp
                          key={index}
                          langUser={langUser}
                          {...field}
                        />
                      );
                    }

                    return null;
                  })}
                </FormInnerCustomsFields>

                {showAutoFlagReport && (
                  <Field
                    name="automaticReport"
                    type="checkbox"
                    component={FormCheckbox}
                    label={t('onboarding.form.automaticReport')}
                  />
                )}
                <div style={{height: '20px'}}></div>
                {submitError && <FormError>{submitError}</FormError>}
                <Button loading={submitting} type="submit">
                  {t('onboarding.form.submit')}
                </Button>
              </form>
            );
          }}
        ></Form>
      </>
    );
  };
  return (
    <Container>
      <Intro>
        <h1>{t('onboarding.title')}</h1>
        <p>{t('onboarding.subtitle')}</p>
      </Intro>
      <Main>
        <Box>
          <LightningImage src={Lightning} alt="Lightning" />
          <BoxInner>
            <FormWrapper>{TheForm()}</FormWrapper>
          </BoxInner>
        </Box>
      </Main>
    </Container>
  );
}

type MultipleChoiceCompProps = {
  langUser: LangUserT;
  name: string;
  required?: boolean;
  labelIt: string;
  labelEn: string;
  labelEs: string;
  labelPl: string;
  options: {
    keyItem: string;
    labelIt: string;
    labelEn: string;
    labelEs: string;
    labelPl: string;
  }[];
};

const MultipleChoiceComp = (props: MultipleChoiceCompProps) => {
  const {errors, touched} = useFormState();
  const {t} = useTranslation();

  const error = touched?.[props.name] && errors?.[props.name];

  const labels = {
    it: props.labelIt,
    en: props.labelEn,
    es: props.labelEs,
    pl: props.labelPl,
  };
  return (
    <MultipleChoiceField>
      <FormInputLabel>{labels[props.langUser]}</FormInputLabel>
      <MultipleChoiceFieldPanel error={error}>
        {props.options.length > 0 ? (
          props.options.map((el, i) => {
            const optionLabels = {
              it: el.labelIt,
              en: el.labelEn,
              es: el.labelEs,
              pl: el.labelPl,
            };

            return (
              <Field
                key={i}
                name={props.name}
                type="radio"
                component={FormRadioButton}
                label={optionLabels[props.langUser]}
                value={el.keyItem}
                validate={props.required ? Validation.required : () => false}
              />
            );
          })
        ) : (
          <p>Ops... Nessuna domanda è stata inserita.</p>
        )}
      </MultipleChoiceFieldPanel>
      {error && <FormError>{t(errors[props.name])}</FormError>}
    </MultipleChoiceField>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: ${(props) => props.theme.sizes.mw};
  margin: 0 auto;
  min-height: 100%;
  padding: 50px 40px;

  @media ${(props) => props.theme.breakpoints.phone} {
    padding: 20px 0;
  }

  h1 {
    font-size: 5rem;
    font-family: 'Bossa Bold', sans-serif;
    color: ${(props) => props.theme.colors.violet500};
    margin-bottom: 24px;

    @media ${(props) => props.theme.breakpoints.landscape} {
      font-size: 4rem;
    }

    @media ${(props) => props.theme.breakpoints.tablet} {
      font-size: 3rem;
    }
  }
`;

const Intro = styled.div`
  margin-bottom: 56px;
  padding: 20px;
`;

const Main = styled.div`
  width: 100%;
`;

const Box = styled.div`
  position: relative;
  width: 80%;

  @media ${(props) => props.theme.breakpoints.tablet} {
    width: 100%;
  }
`;

const BoxInner = styled.div`
  position: relative;
  width: 100%;
  min-height: 100%;
  background-color: ${(props) => props.theme.colors.orange100};
  padding: 0 80px;
  margin-top: 150px;
  margin-bottom: 120px;
  z-index: 1;

  @media ${(props) => props.theme.breakpoints.landscape} {
    padding: 0 60px 0 50px;
  }

  @media ${(props) => props.theme.breakpoints.phone} {
    clip-path: none;
    padding: 0 20px;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 0;
    width: 100%;
    height: 80px;
    clip-path: polygon(0 0, 100% 100%, 100% 100%, 0 100%);
    background-color: ${(props) => props.theme.colors.orange100};
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 80px;
    clip-path: polygon(0 0, 100% 0, 0 100%, 0 100%);
    background-color: ${(props) => props.theme.colors.orange100};
    z-index: -2;
  }
`;

const LightningImage = styled.img`
  position: absolute;
  width: 50px;
  top: 0;
  right: 10%;
  transform: translateY(-50%);
  z-index: 2;

  @media ${(props) => props.theme.breakpoints.tablet} {
    top: -20px;
    width: 30px;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const FormInner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 32px;
  align-items: flex-start;

  @media ${(props) => props.theme.breakpoints.phone} {
    grid-template-columns: 1fr;
  }
`;

const FormInnerCustomsFields = styled.div``;

const MultipleChoiceField = styled.div``;

const MultipleChoiceFieldPanel = styled.div<{error?: boolean}>`
  display: block;
  width: 100%;
  padding: 20px 16px;
  margin-bottom: 24px;
  border-radius: 8px;
  border: solid 1px;
  font-size: 16px;
  font-family: inherit;
  background-color: ${(props) => props.theme.colors.gray100};
  color: ${(props) => props.theme.colors.dark800};
  border-color: ${(p) =>
    p.error ? p.theme.colors.red : p.theme.colors.gray300};
`;
